class PackPriceCalculator extends HTMLElement {
  static instances = new Set();
  static observerStarted = false;
  static _debounceTimer = null;

  // Cache last known good ratio/plan so newly-created instances (after a
  // variant swap re-renders the DOM) can paint immediately instead of blank.
  static lastGood = null; // { ratio, symbol, planLabel }

  connectedCallback() {
    this.textEl = this.querySelector('[data-pack-price-text]');
    this.unitLabel = this.dataset.unitLabel || 'unit';
    this.variantId = this.dataset.variantId;
    this.variantMap = this.loadVariantMap();
    this.hasSubscription = this.detectHasSubscription();

    PackPriceCalculator.instances.add(this);

    if (!PackPriceCalculator.observerStarted) {
      PackPriceCalculator.observerStarted = true;

      const observer = new MutationObserver(() => PackPriceCalculator.scheduleUpdateAll(150));
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      document.addEventListener('change', (e) => {
        if (e.target?.name === 'selling_plan') PackPriceCalculator.scheduleUpdateAll(50);
        if (e.target.closest('[data-product-options]')) PackPriceCalculator.scheduleUpdateAll(300);
      });
    }

    // Paint immediately with cached value (no blank flash), then try a fresh read.
    this.renderFromCache();
    PackPriceCalculator.scheduleUpdateAll(0);
  }

  disconnectedCallback() {
    PackPriceCalculator.instances.delete(this);
  }

  loadVariantMap() {
    const mapScript = document.querySelector('[data-pack-variant-map]');
    if (!mapScript) return {};
    try {
      return JSON.parse(mapScript.textContent);
    } catch (err) {
      console.error('pack-price-calculator: variant map parse error', err);
      return {};
    }
  }

  static scheduleUpdateAll(delay = 150) {
    clearTimeout(PackPriceCalculator._debounceTimer);
    PackPriceCalculator._debounceTimer = setTimeout(() => {
      PackPriceCalculator.instances.forEach((instance) => instance.update());
    }, delay);
  }

  getVariantInput() {
    return (
      this.closest('form')?.querySelector('input[name="id"]') ||
      document.querySelector('form[action*="/cart/add"] input[name="id"]')
    );
  }

  getActiveOption() {
    const wrapper = document.querySelector('.appstle_subscription_wrapper');
    if (!wrapper) return null;
    const checked = wrapper.querySelector('input[name="selling_plan"]:checked');
    return checked ? checked.closest('.appstle_subscription_wrapper_option') : null;
  }

  // Figure out whether THIS variant supports subscription at all, using the
  // raw variant JSON that swatch.liquid already embeds on the page:
  //   <script type="application/json" data-resource="{input_id}">{{ value.variant | json }}</script>
  // Shopify's variant object includes `selling_plan_allocations` — a variant
  // that isn't part of any selling plan group has an empty array here.
  detectHasSubscription() {
    const scripts = document.querySelectorAll('script[type="application/json"][data-resource]');
    for (const script of scripts) {
      let data;
      try {
        data = JSON.parse(script.textContent);
      } catch (err) {
        continue; // malformed/unrelated resource block, skip it
      }
      if (data && String(data.id) === String(this.variantId)) {
        return Array.isArray(data.selling_plan_allocations) && data.selling_plan_allocations.length > 0;
      }
    }
    // Couldn't find a matching resource block — unknown, not "unavailable".
    // Don't block legitimate updates on a lookup miss.
    return true;
  }

  ownHasNoSubscription() {
    return this.hasSubscription === false;
  }

  // Paint using own qty/price + last known good ratio, so there's never a blank frame.
  renderFromCache() {
    if (!this.textEl || !PackPriceCalculator.lastGood) return;
    const ownData = this.variantMap[this.variantId];
    if (!ownData || !ownData.qty || !ownData.price) return;

    const { ratio, symbol, planLabel } = PackPriceCalculator.lastGood;

    if (planLabel === 'Subscription' && this.ownHasNoSubscription()) {
      this.textEl.textContent = 'Subscription not available';
      return;
    }

    const ownRawPrice = ownData.price / 100;
    const perUnit = (ownRawPrice * ratio) / ownData.qty;
    this.textEl.textContent = `${symbol}${perUnit.toFixed(2)} per ${this.unitLabel}`;
  }

  update() {
    if (!this.textEl) return;

    const ownData = this.variantMap[this.variantId];
    if (!ownData || !ownData.qty || ownData.qty <= 0 || !ownData.price) {
      return; // don't blank out — leave whatever is currently shown
    }

    const variantInput = this.getVariantInput();
    const activeVariantId = variantInput?.value;
    const activeData = activeVariantId ? this.variantMap[activeVariantId] : null;

    const activeOption = this.getActiveOption();
    const priceEl = activeOption?.querySelector('.appstle_subscription_amount');

    // Fresh price not available yet (mid re-render) — keep showing last good value.
    if (!priceEl || !activeData || !activeData.price) {
      this.renderFromCache();
      return;
    }

    const isSubscription = !!activeOption.querySelector('.appstle_subscription_radio_wrapper');

    // Currently selected plan on the page is "Subscription", but THIS
    // swatch's own variant does not support subscription at all — don't
    // project a fake price onto it, tell the user plainly instead.
    if (isSubscription && this.ownHasNoSubscription()) {
      this.textEl.textContent = 'Subscription not available';
      return;
    }

    const rawText = priceEl.textContent.trim();
    const symbol = (rawText.match(/^[^\d]+/) || [''])[0].trim();
    const displayedNumeric = parseFloat(rawText.replace(/[^0-9.,]/g, '').replace(',', '.'));
    if (isNaN(displayedNumeric)) {
      this.renderFromCache();
      return;
    }

    const activeRawPrice = activeData.price / 100;
    const ratio = activeRawPrice > 0 ? displayedNumeric / activeRawPrice : 1;

    const planLabel = isSubscription ? 'Subscription' : 'One-Time';

    // Cache this as the last known good state for future instances/renders.
    PackPriceCalculator.lastGood = { ratio, symbol, planLabel };

    const ownRawPrice = ownData.price / 100;
    const perUnit = (ownRawPrice * ratio) / ownData.qty;

    this.textEl.textContent = `${symbol}${perUnit.toFixed(2)} per ${this.unitLabel}`;
  }
}

if (!customElements.get('pack-price-calculator')) {
  customElements.define('pack-price-calculator', PackPriceCalculator);
}